import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Lock,
  FileDown,
  Award,
  PlayCircle,
  Sparkles,
} from 'lucide-react';
import { Markdown } from '@/client/components/cms/Markdown';
import { api, ApiError } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import type { PortalCourseDetail, PortalEpisode, PortalEpisodeStatus } from '@/client/types/courses';
import { SubmitReviewCard } from '@/client/features/reviews/SubmitReviewCard';
import { CertificateDownloadButton } from '@/client/features/certificates/CertificateDownloadButton';
import { cn } from '@/client/lib/utils';

export function PortalCourseDetailPage() {
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const showWelcome = searchParams.get('welcome') === '1';
  const slug = params.slug ?? '';
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  const query = useQuery({
    queryKey: ['portal', 'course', slug],
    queryFn: () => api<PortalCourseDetail>(`/api/v1/me/enrollments/${slug}`),
    enabled: Boolean(slug),
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="dr-card-static h-40 animate-shimmer" />
        <div className="dr-card-static h-20 animate-shimmer" />
        <div className="dr-card-static h-20 animate-shimmer" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <div className="dr-card-static p-12 text-center">
          <h1 className="font-display text-h2 text-primary-dark">
            {t('errors.not_found_title')}
          </h1>
          <Link
            to={`/${locale}/portal/kurzy`}
            className="mt-6 inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary hover:gap-2.5 transition-all"
          >
            <ArrowLeft size={14} />
            {t('portal.nav_courses')}
          </Link>
        </div>
      );
    }
    return (
      <div className="dr-card-static p-6 font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!query.data) return null;
  const { course, episodes, enrollment } = query.data;
  const courseCompleted = enrollment.completedAt !== null;

  const totalEpisodes = episodes.length;
  const completedEpisodes = episodes.filter((e) => e.status === 'completed').length;
  const pct =
    totalEpisodes > 0 ? Math.round((completedEpisodes / totalEpisodes) * 100) : 0;
  const nextEpisode =
    episodes.find((e) => e.status === 'available') ??
    episodes.find((e) => e.status !== 'completed') ??
    null;

  return (
    <div className="space-y-6 animate-fade-up">
      <Link
        to={`/${locale}/portal/kurzy`}
        className="inline-flex items-center gap-1.5 font-sans text-body-small font-medium text-primary hover:gap-2.5 transition-all"
      >
        <ArrowLeft size={14} />
        {t('portal.nav_courses')}
      </Link>

      {showWelcome && (
        <section className="dr-card-static relative overflow-hidden p-6 sm:p-8">
          <div
            className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(193,167,226,0.35) 0%, transparent 65%)',
              filter: 'blur(30px)',
            }}
            aria-hidden
          />
          <div className="relative flex items-start gap-4">
            <div className="relative shrink-0 h-16 w-16 sm:h-20 sm:w-20">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'conic-gradient(from 220deg, rgba(193,167,226,0.40), rgba(117,59,189,0.18), rgba(193,167,226,0.40))',
                  filter: 'blur(6px)',
                }}
                aria-hidden
              />
              <img
                src="/brand/mascot-purple.svg"
                alt=""
                className="relative h-full w-full object-contain animate-float"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <div>
              <p className="dr-eyebrow">
                <Sparkles size={12} className="inline -mt-0.5 mr-1" />
                {t('portal_extras.welcome_banner_eyebrow', { defaultValue: 'Vitaj' })}
              </p>
              <h2 className="mt-1 font-display text-h3 text-primary-dark">
                {t('portal_extras.welcome_banner_title', { title: course.title })}
              </h2>
              <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed">
                {t('portal_extras.welcome_banner_body')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Hero with course title + progress + Continue CTA */}
      <section className="dr-card-static relative overflow-hidden p-6 sm:p-8 lg:p-10">
        <div
          className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(193,167,226,0.30) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative">
          <p className="dr-eyebrow">{t('portal.nav_courses')}</p>
          <h1 className="mt-2 font-display text-h1 text-primary-dark">
            {course.title}
          </h1>
          {course.promise && (
            <p className="mt-3 font-display text-body-large text-text-secondary leading-relaxed max-w-2xl">
              {course.promise}
            </p>
          )}

          <div className="mt-6 grid gap-5 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-sans text-body-small text-text-secondary">
                  {t('courses.portal_progress', {
                    completed: completedEpisodes,
                    total: totalEpisodes,
                  })}
                </span>
                <span className="font-sans text-body-small font-semibold text-primary-dark">
                  {pct}%
                </span>
              </div>
              <div className="h-2 rounded-pill bg-glass-border overflow-hidden">
                <div
                  className="h-full rounded-pill transition-all duration-700"
                  style={{
                    width: `${pct}%`,
                    background:
                      'linear-gradient(90deg, #c1a7e2 0%, #753bbd 50%, #512d6d 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
                  }}
                />
              </div>
            </div>
            {nextEpisode && !courseCompleted && (
              <Link
                to={`/${locale}/portal/kurzy/${slug}/lekcia/${nextEpisode.displayOrder}`}
                className="inline-flex items-center justify-center gap-2 rounded-pill bg-gradient-to-br from-primary to-primary-dark text-white h-11 px-5 font-sans text-body font-medium shadow-[0_4px_16px_rgba(117,59,189,0.28),inset_0_1px_0_rgba(255,255,255,0.18)] hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38)] transition-all"
              >
                <PlayCircle size={16} />
                {completedEpisodes > 0
                  ? t('courses.continue_cta')
                  : t('courses.open_lesson')}
                <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </section>

      {courseCompleted && (
        <section className="dr-card-static relative overflow-hidden p-6 sm:p-8">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(135deg, rgba(45,139,85,0.10) 0%, transparent 60%)',
            }}
            aria-hidden
          />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-success/20 to-success/5 border border-success/20 text-success shrink-0">
              <Award size={26} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-h3 text-success">
                {t('courses.course_completed_title')}
              </h2>
              <p className="mt-1 font-sans text-body text-text-secondary">
                {t('courses.course_completed_body')}
              </p>
            </div>
            <CertificateDownloadButton courseSlug={course.slug} />
          </div>
        </section>
      )}

      <details className="dr-card-static group/details p-5 sm:p-6 [&_summary::-webkit-details-marker]:hidden">
        <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
          <span className="font-sans text-body font-medium text-primary-dark">
            {t('courses.about_course', { defaultValue: 'O kurze' })}
          </span>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-pill bg-white/70 border border-glass-border-strong text-primary transition-transform group-open/details:rotate-180">
            <ArrowRight size={14} className="rotate-90" />
          </span>
        </summary>
        <div className="mt-5 pt-5 border-t border-glass-border">
          <Markdown source={course.descriptionMd} />
        </div>
      </details>

      <section>
        <div className="flex items-center justify-between gap-3 mb-4">
          <p className="dr-eyebrow">
            {t('courses.episodes_eyebrow', { defaultValue: 'Epizódy' })}
          </p>
          <span className="font-sans text-caption text-text-muted">
            {completedEpisodes}/{totalEpisodes}
          </span>
        </div>
        <ol className="space-y-3 stagger-children">
          {episodes.map((episode) => (
            <EpisodeRow key={episode.id} episode={episode} slug={slug} locale={locale} />
          ))}
        </ol>
      </section>

      {courseCompleted && (
        <div className="mt-2">
          <SubmitReviewCard enrollmentId={enrollment.id} />
        </div>
      )}
    </div>
  );
}

function EpisodeRow({
  episode,
  slug,
  locale,
}: {
  episode: PortalEpisode;
  slug: string;
  locale: Locale;
}) {
  const { t } = useTranslation();
  const isLocked = episode.status === 'locked';
  const lessonUrl = `/${locale}/portal/kurzy/${slug}/lekcia/${episode.displayOrder}`;

  const content = (
    <div className="flex items-center justify-between gap-4 p-5 sm:p-6">
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <EpisodeStatusIcon status={episode.status} />
        <div className="min-w-0">
          <p className="font-sans text-caption uppercase tracking-[0.14em] text-text-muted">
            {t('courses.episode_label', { number: episode.displayOrder })}
          </p>
          <h3 className="mt-1 font-display text-h4 text-primary-dark truncate">
            {episode.title}
          </h3>
          {episode.resources.length > 0 && (
            <p className="mt-1.5 inline-flex items-center gap-1.5 font-sans text-caption text-text-secondary">
              <FileDown size={12} />
              {episode.resources.length}× {t('courses.material', { defaultValue: 'materiál' })}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <EpisodeStatusBadge status={episode.status} unlockAfterDays={episode.unlockAfterDays} />
        {!isLocked && (
          <span className="hidden sm:inline-flex items-center justify-center h-9 w-9 rounded-pill bg-white/70 border border-glass-border-strong text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all">
            <ArrowRight size={14} />
          </span>
        )}
      </div>
    </div>
  );

  return (
    <li
      className={cn(
        'group overflow-hidden',
        isLocked
          ? 'rounded-2xl bg-white/55 backdrop-blur-md border border-glass-border opacity-75'
          : 'dr-card hover:-translate-y-0.5',
      )}
    >
      {isLocked ? content : <Link to={lessonUrl} className="block">{content}</Link>}
    </li>
  );
}

function EpisodeStatusIcon({ status }: { status: PortalEpisodeStatus }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-success/20 to-success/5 border border-success/20 text-success shrink-0">
        <CheckCircle2 size={18} />
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/70 border border-glass-border-strong text-text-muted shrink-0">
        <Lock size={16} />
      </span>
    );
  }
  return (
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 text-primary shrink-0">
      <PlayCircle size={18} />
    </span>
  );
}

function EpisodeStatusBadge({
  status,
  unlockAfterDays,
}: {
  status: PortalEpisodeStatus;
  unlockAfterDays: number;
}) {
  const { t } = useTranslation();
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center rounded-pill border border-success/25 bg-success/12 px-3 py-1 font-sans text-caption font-medium text-success">
        {t('courses.portal_episode_completed')}
      </span>
    );
  }
  if (status === 'locked') {
    return (
      <span className="inline-flex items-center rounded-pill border border-warning/30 bg-warning/12 px-3 py-1 font-sans text-caption font-medium text-[#7a5500]">
        {t('courses.portal_episode_locked', { count: unlockAfterDays })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-pill border border-primary/25 bg-primary-light/30 px-3 py-1 font-sans text-caption font-medium text-primary-dark">
      {t('courses.open_lesson')}
    </span>
  );
}
